import { DeviceOSType } from '@/api/user-portal-api-generated';
import {
  getMobileAppDeepLinkScheme,
  getMobileAppLinkHost,
  isValidEnvValue,
} from '@/config/env-config';

const DEEP_LINK_SCHEME_PLACEHOLDER = '__MOBILE_APP_DEEP_LINK_SCHEME__';
const DEEP_LINK_SCHEME_FALLBACK = 'newcoreauth-dev';
const LINK_HOST_PLACEHOLDER = '__MOBILE_APP_LINK_HOST__';
const ONBOARDING_DEEP_LINK_PATH = 'onboarding-entry';
const PAIRING_DEEP_LINK_PATH = 'token-entry';
const INSTALL_REFERRER_TOKEN_PARAM = 'nc_token';
const INSTALL_REFERRER_PAIRING_PARAM = 'nc_pairing';

export function getDeepLinkScheme() {
  const runtimeScheme = getMobileAppDeepLinkScheme();

  if (isValidEnvValue(runtimeScheme, DEEP_LINK_SCHEME_PLACEHOLDER)) {
    return runtimeScheme;
  }

  return DEEP_LINK_SCHEME_FALLBACK;
}

export function getAppLinkHost() {
  const runtimeHost = getMobileAppLinkHost();

  return isValidEnvValue(runtimeHost, LINK_HOST_PLACEHOLDER)
    ? runtimeHost
    : null;
}

export function getMobilePlatform() {
  if (typeof navigator === 'undefined') {
    return null;
  }

  const userAgent = navigator.userAgent ?? '';

  if (/android/i.test(userAgent)) {
    return DeviceOSType.ANDROID;
  }

  if (isIOSDevice(userAgent)) {
    return DeviceOSType.IOS;
  }

  return null;
}

export function isMobileDevice() {
  return getMobilePlatform() !== null;
}

// iPadOS sends a Mac user agent by default; only touch support tells it apart
function isIOSDevice(userAgent: string) {
  return (
    /iphone|ipad|ipod/i.test(userAgent) ||
    (/macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1)
  );
}

export function createOnboardingDeepLinkUrl(token: string) {
  return `${getDeepLinkScheme()}://${ONBOARDING_DEEP_LINK_PATH}?token=${encodeURIComponent(token)}`;
}

export function createPairingDeepLinkUrl(pairingCode?: string) {
  return `${getDeepLinkScheme()}://${PAIRING_DEEP_LINK_PATH}${pairingQuery(pairingCode)}`;
}

// Builds without a claimed link host (dev) only answer their own scheme
export function createOnboardingOpenAppUrl(token: string) {
  const linkHost = getAppLinkHost();

  if (!linkHost) {
    return createOnboardingDeepLinkUrl(token);
  }

  return `https://${linkHost}/${ONBOARDING_DEEP_LINK_PATH}?token=${encodeURIComponent(token)}&${createOnboardingInstallReferrer(token)}`;
}

export function createPairingOpenAppUrl(pairingCode?: string) {
  const linkHost = getAppLinkHost();

  if (!linkHost) {
    return createPairingDeepLinkUrl(pairingCode);
  }

  const base = `https://${linkHost}/${PAIRING_DEEP_LINK_PATH}${pairingQuery(pairingCode)}`;

  return pairingCode
    ? `${base}&${createPairingInstallReferrer(pairingCode)}`
    : base;
}

export function createOnboardingInstallReferrer(token: string) {
  return new URLSearchParams({
    [INSTALL_REFERRER_TOKEN_PARAM]: token,
  }).toString();
}

export function createPairingInstallReferrer(pairingCode: string) {
  return new URLSearchParams({
    [INSTALL_REFERRER_PAIRING_PARAM]: pairingCode,
  }).toString();
}

function pairingQuery(pairingCode?: string) {
  return pairingCode ? `?pairingCode=${encodeURIComponent(pairingCode)}` : '';
}
